import React, { useState, useEffect } from "react";
import Api from "../axiosConfig";

const OptInModal = ({ onSuccess, onError }) => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  // Check if user has dismissed the banner before
  useEffect(() => {
    const hasDismissed = localStorage.getItem("optin-banner-dismissed");
    if (!hasDismissed) {
      // Show banner after a short delay to let page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async () => {
    if (!phone.trim()) {
      setError("Phone number is required");
      return;
    }

    if (!/^\+?[1-9]\d{1,14}$/.test(phone.replace(/\s/g, ""))) {
      setError("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await Api.post("/v1/optform", {
        phone: phone,
        consent: {
          sms: true,
          email: false,
          marketing: false,
        },
      });

      if (response.data.success) {
        setPhone("");
        if (onSuccess) onSuccess(response.data);
        handleClose();
      } else {
        setError(response.data.message || "Failed to subscribe");
      }
    } catch (err) {
      console.error("Opt-in error:", err);
      setError(
        err.response?.data?.message || "An error occurred. Please try again."
      );
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Mark as dismissed in localStorage
    localStorage.setItem("optin-banner-dismissed", "true");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg animate-slide-up">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zM6 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-gray-700 font-medium">
                Get SMS updates
              </span>
            </div>

            <div className="flex items-center gap-3 flex-1 max-w-md">
              <input
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (error) setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
                placeholder="Enter your phone number"
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />

              <button
                type="submit"
                disabled={loading}
                onClick={handleSubmit}
                className="px-4 py-1.5 bg-primary-dark hover:bg-primary disabled:bg-gray-400 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

        <div className="mt-2 text-xs text-gray-500">
          By subscribing, you consent to receive SMS messages. Message & data
          rates may apply. Reply STOP to opt out.
        </div>
      </div>
    </div>
  );
};

export default OptInModal;
